// src/pages/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrders } from '../firebase/services';

const STATUS_CONFIG = {
  'Created':          { label: 'Order Placed',    color: '#2563EB', track: 0  },
  'Accepted':         { label: 'Preparing',        color: '#D97706', track: 1  },
  'Ready':            { label: 'Ready',            color: '#7C3AED', track: 2  },
  'Order Picked':     { label: 'Picked Up',        color: '#059669', track: 3  },
  'Out for Delivery': { label: 'On the Way',       color: '#EA580C', track: 4  },
  'Delivered':        { label: 'Delivered',        color: '#16A34A', track: 5  },
  'Cancelled':        { label: 'Cancelled',        color: '#DC2626', track: -1 },
};

const TRACK = ['Created','Accepted','Ready','Order Picked','Out for Delivery','Delivered'];
const TRACK_SHORT = ['Placed','Prep','Ready','Picked','Enroute','Done'];

const fmtDate = ts => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
};
const fmtTime = ts => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
};

export default function OrdersPage() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const [orders,    setOrders]   = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [expanded,  setExpanded] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    getOrders(user.uid)
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <>
      <div className="op-root">

        {/* ── Header ── */}
        <div className="op-topbar">
          <div className="op-topbar-inner">
            <div>
              <p className="op-eyebrow">
                <Link to="/" className="op-eyebrow-link">Home</Link>
                <span className="op-eyebrow-sep"> / </span>Account
              </p>
              <h1 className="op-heading">My Orders</h1>
            </div>
            {!loading && orders.length > 0 && (
              <div className="op-count-pill">{orders.length} orders</div>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="op-content">

          {/* Skeletons */}
          {loading && (
            <div className="op-skeleton-wrap">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="op-skel" style={{ animationDelay:`${i*0.1}s` }}>
                  <div className="op-skel-bar op-skel-a" />
                  <div className="op-skel-bar op-skel-b" />
                  <div className="op-skel-bar op-skel-c" />
                </div>
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && orders.length === 0 && (
            <div className="op-empty">
              <div className="op-empty-num">0</div>
              <p className="op-empty-msg">No orders yet. Start ordering from our vendors.</p>
              <Link to="/" className="op-empty-cta">Browse Vendors →</Link>
            </div>
          )}

          {/* Order list */}
          {!loading && orders.length > 0 && (
            <div className="op-list">
              {orders.map((order, idx) => {
                const status      = order.status || 'Created';
                const cfg         = STATUS_CONFIG[status] || STATUS_CONFIG['Created'];
                const isOpen      = expanded === order.id;
                const items       = order.orderDetails || [];
                const total       = order.totalAmount || 0;
                const orderId     = order.OrderId || order.id;
                const isCOD       = (order.paymentMethod||'').toUpperCase() === 'COD';
                const trackIdx    = TRACK.indexOf(status);
                const isCancelled = status === 'Cancelled';
                const pct         = trackIdx >= 0 ? Math.round((trackIdx / (TRACK.length-1)) * 100) : 0;

                return (
                  <div
                    key={order.id}
                    className={`op-card ${isOpen ? 'op-card-active' : ''}`}
                    style={{ '--i': idx }}
                  >
                    {/* ─ Status accent bar ─ */}
                    <div className="op-accent" style={{ background: cfg.color }} />

                    {/* ─ Main row ─ */}
                    <div className="op-row" onClick={() => setExpanded(isOpen ? null : order.id)}>

                      {/* Left: images + info */}
                      <div className="op-row-left">
                        {/* Stacked images */}
                        <div className="op-thumbs">
                          {items.slice(0,4).map((item, j) => (
                            <div key={j} className="op-thumb" style={{ zIndex: 4-j, right: `${j*10}px` }}>
                              {(item.ImageBlob||item.image)
                                ? <img src={item.ImageBlob||item.image} alt="" />
                                : <span className="op-thumb-ph">🍽</span>}
                            </div>
                          ))}
                          {items.length === 0 && (
                            <div className="op-thumb">
                              <span className="op-thumb-ph">🛍</span>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="op-info">
                          <div className="op-id">{orderId}</div>
                          <div className="op-restaurant">{order.vendorName || 'Order'}</div>
                          <div className="op-date">{fmtDate(order.createdAt)} · {fmtTime(order.createdAt)}</div>
                        </div>
                      </div>

                      {/* Right: status + amount */}
                      <div className="op-row-right">
                        <div className="op-status-tag" style={{ color: cfg.color, background: cfg.color + '14', border: `1px solid ${cfg.color}28` }}>
                          {cfg.label}
                        </div>
                        <div className="op-amount">₹{total.toFixed(2)}</div>
                        <div className="op-pay-tag">{isCOD ? 'COD' : 'Paid'}</div>
                        <div className={`op-toggle ${isOpen ? 'op-toggle-open' : ''}`}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                        </div>
                      </div>
                    </div>

                    {/* ─ Progress bar ─ */}
                    {!isCancelled && (
                      <div className="op-progress-wrap">
                        <div className="op-progress-bg">
                          <div className="op-progress-fg" style={{ width: `${pct}%`, background: cfg.color }} />
                        </div>
                        <div className="op-steps">
                          {TRACK.map((_, si) => (
                            <div key={si} className={`op-step ${si <= trackIdx ? 'op-step-done' : ''} ${si === trackIdx ? 'op-step-now' : ''}`}>
                              <div className="op-step-dot" style={si <= trackIdx ? { background: cfg.color, borderColor: cfg.color } : {}} />
                              <span className="op-step-lbl">{TRACK_SHORT[si]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isCancelled && (
                      <div className="op-cancelled">Order cancelled</div>
                    )}

                    {/* ─ Expanded panel ─ */}
                    {isOpen && (
                      <div className="op-panel">

                        {/* Items */}
                        <div className="op-panel-section">
                          <div className="op-panel-label">Items · {items.length}</div>
                          <div className="op-items">
                            {items.map((item, j) => (
                              <div key={j} className="op-item">
                                <div className="op-item-pic">
                                  {(item.ImageBlob||item.image)
                                    ? <img src={item.ImageBlob||item.image} alt="" />
                                    : <span>🍽</span>}
                                </div>
                                <div className="op-item-text">
                                  <div className="op-item-name">{item.ItemName || item.name}</div>
                                  <div className="op-item-sub">×{item.quantity || item.qty || 1} · ₹{item.ItemPrice || item.price}</div>
                                </div>
                                <div className="op-item-price">
                                  ₹{((item.ItemPrice||item.price||0) * (item.quantity||item.qty||1)).toFixed(2)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Two-col: bill + address */}
                        <div className="op-panel-two">

                          {/* Bill */}
                          <div className="op-panel-section">
                            <div className="op-panel-label">Bill</div>
                            <div className="op-bill">
                              {[
                                ['Subtotal',  `₹${(order.itemSubTotal||0).toFixed(2)}`],
                                ['Delivery',  order.deliveryCharge===0 ? 'Free' : `₹${order.deliveryCharge||0}`],
                                ['Packing',   `₹${order.vendorPackingCharge||0}`],
                              ].map(([k,v]) => (
                                <div key={k} className="op-bill-row">
                                  <span>{k}</span><span className={v==='Free' ? 'op-green' : ''}>{v}</span>
                                </div>
                              ))}
                              <div className="op-bill-divider" />
                              <div className="op-bill-total">
                                <span>Total</span>
                                <span className="op-bill-total-val">₹{total.toFixed(2)}</span>
                              </div>
                              {order.rewardPointsEarned > 0 && (
                                <div className="op-points">+{order.rewardPointsEarned} pts earned</div>
                              )}
                            </div>
                          </div>

                          {/* Address */}
                          {order.address && (
                            <div className="op-panel-section">
                              <div className="op-panel-label">Delivery</div>
                              <div className="op-addr">
                                <div className="op-addr-name">{order.address.receiverName || order.customerName}</div>
                                <div className="op-addr-line">{order.address.address}</div>
                                <div className="op-addr-phone">{order.address.phone || order.phoneNumber}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Timeline */}
                        {(order.statusHistory||[]).length > 0 && (
                          <div className="op-panel-section">
                            <div className="op-panel-label">Timeline</div>
                            <div className="op-timeline">
                              {order.statusHistory.map((h, j) => {
                                const hTime = h.createdAtHis?.toDate ? h.createdAtHis.toDate() : h.createdAtHis instanceof Date ? h.createdAtHis : null;
                                const hcfg  = STATUS_CONFIG[h.status] || {};
                                const last  = j === order.statusHistory.length - 1;
                                return (
                                  <div key={j} className={`op-tl ${last ? 'op-tl-last' : ''}`}>
                                    <div className="op-tl-track">
                                      <div className="op-tl-dot" style={{ background: hcfg.color || '#ccc' }} />
                                      {!last && <div className="op-tl-line" />}
                                    </div>
                                    <div className="op-tl-body">
                                      <div className="op-tl-title">{h.mainStatus || h.status}</div>
                                      {h.description && <div className="op-tl-desc">{h.description}</div>}
                                      {hTime && <div className="op-tl-time">{fmtDate(hTime)} · {fmtTime(hTime)}</div>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700&family=Inter:wght@300;400;500&display=swap');

        /* ── Root ── */
        .op-root {
          min-height: 100vh;
          background: #F7F6F3;
          font-family: 'Inter', sans-serif;
          padding-top: 68px;
          --green: #16A34A;
          --border: #E5E2DC;
          --text: #111;
          --text2: #666;
          --text3: #999;
          --radius: 12px;
        }

        /* ── Topbar ── */
        .op-topbar {
          background: #fff;
          border-bottom: 1px solid var(--border);
          padding: 22px 0 18px;
          position: sticky; top: 68px; z-index: 30;
        }
        .op-topbar-inner {
          max-width: 760px; margin: 0 auto; padding: 0 20px;
          display: flex; align-items: flex-end; justify-content: space-between;
          gap: 12px;
        }
        .op-eyebrow {
          font-size: 11px; color: var(--text3);
          letter-spacing: 0.06em; text-transform: uppercase;
          margin: 0 0 4px;
        }
        .op-eyebrow-link { color: var(--text3); text-decoration: none; }
        .op-eyebrow-link:hover { color: var(--text); }
        .op-eyebrow-sep { color: var(--text3); }
        .op-heading {
          font-family: 'Syne', sans-serif;
          font-size: 30px; font-weight: 700;
          color: var(--text); margin: 0; line-height: 1;
          letter-spacing: -0.5px;
        }
        .op-count-pill {
          font-size: 11px; font-weight: 500;
          color: var(--text2); background: #F0EDE8;
          padding: 5px 12px; border-radius: 20px;
          letter-spacing: 0.04em; flex-shrink: 0;
        }

        /* ── Content ── */
        .op-content {
          max-width: 760px; margin: 0 auto;
          padding: 20px 16px 60px;
        }

        /* ── Skeleton ── */
        .op-skeleton-wrap { display: flex; flex-direction: column; gap: 8px; }
        .op-skel {
          background: #fff; border-radius: var(--radius);
          border: 1px solid var(--border);
          padding: 20px; display: flex; flex-direction: column; gap: 10px;
          animation: op-in 0.3s ease both;
          animation-delay: var(--delay, 0s);
        }
        .op-skel-bar { border-radius: 4px; background: #EEECE8; animation: op-pulse 1.5s ease infinite; }
        .op-skel-a { height: 12px; width: 40%; }
        .op-skel-b { height: 10px; width: 60%; }
        .op-skel-c { height: 8px; width: 30%; }
        @keyframes op-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }

        /* ── Empty ── */
        .op-empty {
          text-align: center; padding: 80px 20px 60px;
          background: #fff; border-radius: var(--radius); border: 1px solid var(--border);
        }
        .op-empty-num {
          font-family: 'Syne', sans-serif;
          font-size: 72px; font-weight: 700;
          color: #E5E2DC; line-height: 1; margin-bottom: 16px;
        }
        .op-empty-msg { font-size: 14px; color: var(--text2); margin: 0 0 24px; }
        .op-empty-cta {
          display: inline-block; padding: 11px 24px;
          background: var(--text); color: #fff;
          border-radius: 8px; font-size: 13px; font-weight: 500;
          text-decoration: none; transition: opacity 0.2s;
        }
        .op-empty-cta:hover { opacity: 0.85; }

        /* ── List ── */
        .op-list { display: flex; flex-direction: column; gap: 8px; }

        /* ── Card ── */
        .op-card {
          background: #fff;
          border-radius: var(--radius);
          border: 1px solid var(--border);
          overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s;
          animation: op-in 0.3s ease both;
          animation-delay: calc(var(--i, 0) * 0.05s);
          position: relative;
        }
        .op-card:hover { border-color: #CCC8C0; }
        .op-card-active { border-color: #111 !important; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
        @keyframes op-in { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

        /* Accent bar */
        .op-accent {
          height: 3px; width: 100%;
          opacity: 0; transition: opacity 0.2s;
        }
        .op-card:hover .op-accent,
        .op-card-active .op-accent { opacity: 1; }

        /* ── Main row ── */
        .op-row {
          display: flex; align-items: center;
          justify-content: space-between;
          gap: 16px; padding: 16px 18px;
          cursor: pointer; flex-wrap: wrap;
        }
        .op-row-left { display: flex; align-items: center; gap: 14px; min-width: 0; flex: 1; }

        /* Thumbnail stack */
        .op-thumbs {
          position: relative;
          width: 52px; height: 44px;
          flex-shrink: 0;
        }
        .op-thumb {
          position: absolute; top: 0;
          width: 40px; height: 40px;
          border-radius: 8px; overflow: hidden;
          border: 2px solid #fff;
          background: #F0EDE8;
          display: flex; align-items: center; justify-content: center;
        }
        .op-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .op-thumb-ph { font-size: 16px; }

        /* Info */
        .op-info { min-width: 0; }
        .op-id {
          font-family: 'Syne', sans-serif;
          font-size: 13px; font-weight: 600;
          color: var(--text); letter-spacing: -0.2px;
          margin-bottom: 2px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .op-restaurant { font-size: 12px; color: var(--text2); font-weight: 500; margin-bottom: 1px; }
        .op-date       { font-size: 11px; color: var(--text3); }

        /* Right side */
        .op-row-right {
          display: flex; align-items: center; gap: 8px;
          flex-shrink: 0;
        }
        .op-status-tag {
          font-size: 10px; font-weight: 600;
          padding: 3px 9px; border-radius: 20px;
          letter-spacing: 0.04em; white-space: nowrap;
        }
        .op-amount {
          font-family: 'Syne', sans-serif;
          font-size: 16px; font-weight: 700;
          color: var(--text); white-space: nowrap;
        }
        .op-pay-tag {
          font-size: 9px; font-weight: 600;
          color: var(--text3); background: #F0EDE8;
          padding: 3px 7px; border-radius: 4px;
          letter-spacing: 0.06em; text-transform: uppercase;
        }
        .op-toggle { color: var(--text3); transition: transform 0.2s, color 0.2s; }
        .op-toggle-open { transform: rotate(180deg); color: var(--text); }

        /* ── Progress ── */
        .op-progress-wrap {
          padding: 0 18px 16px;
          border-top: 1px solid #F4F2EE;
        }
        .op-progress-bg {
          height: 3px; background: #EEECE8;
          border-radius: 3px; margin-bottom: 10px;
          position: relative; overflow: hidden;
        }
        .op-progress-fg {
          position: absolute; top: 0; left: 0; height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
        .op-steps {
          display: flex; justify-content: space-between;
        }
        .op-step {
          display: flex; flex-direction: column; align-items: center; gap: 4px;
        }
        .op-step-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #DDD; border: 1.5px solid #DDD;
          transition: all 0.3s;
        }
        .op-step-done .op-step-dot { background: currentColor; border-color: currentColor; }
        .op-step-now .op-step-dot  { box-shadow: 0 0 0 3px rgba(0,0,0,0.08); }
        .op-step-lbl {
          font-size: 8px; color: var(--text3);
          letter-spacing: 0.04em; white-space: nowrap;
        }
        .op-step-done .op-step-lbl { color: var(--text2); font-weight: 500; }

        /* Cancelled */
        .op-cancelled {
          padding: 8px 18px;
          font-size: 11px; color: #DC2626;
          background: #FEF2F2; border-top: 1px solid #FEE2E2;
          font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase;
        }

        /* ── Expanded panel ── */
        .op-panel {
          border-top: 1px solid #F0EDE8;
          background: #FAFAF8;
          padding: 0 18px 18px;
          animation: op-expand 0.18s ease;
        }
        @keyframes op-expand { from{opacity:0} to{opacity:1} }

        .op-panel-section { padding-top: 16px; }
        .op-panel-label {
          font-size: 9px; font-weight: 600;
          letter-spacing: 0.16em; text-transform: uppercase;
          color: var(--text3); margin-bottom: 10px;
        }

        .op-panel-two {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        /* Items */
        .op-items {
          background: #fff; border: 1px solid var(--border);
          border-radius: 8px; overflow: hidden;
        }
        .op-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-bottom: 1px solid #F4F2EE;
        }
        .op-item:last-child { border-bottom: none; }
        .op-item-pic {
          width: 40px; height: 44px; border-radius: 6px;
          overflow: hidden; flex-shrink: 0; background: #F0EDE8;
          display: flex; align-items: center; justify-content: center; font-size: 16px;
        }
        .op-item-pic img { width:100%; height:100%; object-fit:cover; display:block; }
        .op-item-text  { flex: 1; min-width: 0; }
        .op-item-name  {
          font-size: 12px; font-weight: 500; color: var(--text);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 1px;
        }
        .op-item-sub   { font-size: 11px; color: var(--text3); }
        .op-item-price { font-size: 13px; font-weight: 600; color: var(--text); flex-shrink: 0; }

        /* Bill */
        .op-bill {
          background: #fff; border: 1px solid var(--border);
          border-radius: 8px; padding: 12px;
        }
        .op-bill-row {
          display: flex; justify-content: space-between;
          font-size: 12px; color: var(--text2); margin-bottom: 6px;
        }
        .op-green { color: var(--green) !important; }
        .op-bill-divider { height: 1px; background: var(--border); margin: 8px 0; }
        .op-bill-total {
          display: flex; justify-content: space-between;
          font-size: 13px; font-weight: 600; color: var(--text);
        }
        .op-bill-total-val {
          font-family: 'Syne', sans-serif;
          font-size: 15px; font-weight: 700;
        }
        .op-points {
          margin-top: 8px; font-size: 11px; font-weight: 500;
          color: var(--green); background: #F0FDF4;
          padding: 5px 8px; border-radius: 5px;
        }

        /* Address */
        .op-addr {
          background: #fff; border: 1px solid var(--border);
          border-radius: 8px; padding: 12px;
        }
        .op-addr-name  { font-size: 12px; font-weight: 600; color: var(--text); margin-bottom: 3px; }
        .op-addr-line  { font-size: 11px; color: var(--text2); line-height: 1.55; margin-bottom: 4px; }
        .op-addr-phone { font-size: 11px; color: var(--text3); }

        /* Timeline */
        .op-timeline { padding: 2px 0; }
        .op-tl { display: flex; gap: 10px; }
        .op-tl-track {
          display: flex; flex-direction: column;
          align-items: center; flex-shrink: 0; width: 12px;
        }
        .op-tl-dot {
          width: 10px; height: 10px; border-radius: 50%;
          flex-shrink: 0; margin-top: 2px;
        }
        .op-tl-line {
          width: 1px; flex: 1; background: var(--border);
          margin: 3px 0; min-height: 12px;
        }
        .op-tl-body   { flex: 1; padding-bottom: 14px; }
        .op-tl-last .op-tl-body { padding-bottom: 0; }
        .op-tl-title  { font-size: 12px; font-weight: 500; color: var(--text); margin-bottom: 1px; }
        .op-tl-desc   { font-size: 11px; color: var(--text2); }
        .op-tl-time   { font-size: 10px; color: var(--text3); margin-top: 1px; }

        /* ── Mobile ≤ 600px ── */
        @media (max-width: 600px) {
          .op-root    { padding-top: 60px; }
          .op-topbar  { top: 60px; padding: 16px 0 14px; }
          .op-heading { font-size: 24px; }
          .op-content { padding: 14px 12px 50px; }

          .op-row { padding: 13px 14px; gap: 10px; }
          .op-status-tag { display: none; }
          .op-amount { font-size: 15px; }

          .op-progress-wrap { padding: 0 14px 14px; }
          .op-step-lbl { font-size: 7px; }

          .op-panel { padding: 0 14px 14px; }
          .op-panel-two { grid-template-columns: 1fr; gap: 0; }

          .op-item { padding: 9px 10px; }
          .op-item-pic { width: 36px; height: 40px; }
        }

        @media (max-width: 400px) {
          .op-row-right { gap: 6px; }
          .op-pay-tag { display: none; }
          .op-thumbs { width: 44px; height: 40px; }
          .op-thumb  { width: 36px; height: 36px; }
        }
      `}</style>
    </>
  );
}
